<template>
  <div class="content inset">
    <h2 class="title">
      <awesomeIcon icon="level-up-alt" style="transform:rotate(90deg)" />
      Financiële situatie
      {{ $parent.intake.bedrijfsData.bedrijfsnaam }}
    </h2>
    <customInput
      v-model="modelValue.negatiefVermorgen"
      type="yesno"
      :label="'Heeft het bedrijf een negatief eigen vermogen?'"
    />
    <div class="msg" v-if="modelValue.negatiefVermorgen === 'Yes'">
      Het vermogen dat Doelbeleggen voor u gaat beheren, in combinatie met het overige vermogen
      binnen de B.V., is dan wellicht onvoldoende voor de volledige dekking van de voorziening in
      de B.V.. Dit kan invloed hebben op de haalbaarheid van eventuele toekomstige betalingen.
      Wanneer u hier nog niet met uw accountant over heeft gesproken, adviseren wij u alsnog
      contact met hem of haar op te nemen.
    </div>
    <customInput
      v-model="modelValue.uitgeleend"
      type="yesno"
      :label="'Heeft het bedrijf geld uitgeleend aan een privépersoon?'"
    />
    <customInput
      v-if="modelValue.uitgeleend === 'Yes'"
      v-model="modelValue.uitgeleendU"
      type="yesno"
      :label="'Is het geld aan u geleend?'"
    />
    <customInput
      v-if="
        (
          $parent.intakeAdmin.beleggen.brand === 'BVS'
          || $parent.intakeAdmin.beleggen.brand === 'VMVB'
        )
        || ['bv', 'stichting', 'nv', 'anders'].includes($parent.intake.bedrijfsData.rechtsvorm)
      "
      v-model="modelValue.jaarrekening"
      type="yesno"
      :label="'Heeft u een recente jaarrekening van het bedrijf?'"
    />
    <div v-if="modelValue.jaarrekening === 'Yes'">
      <customInput
        v-model="modelValue.companyFiles"
        type="file"
        :validation="['files']"
        :prefix="'jaarrekening'"
      />
      <div class="msg">
        Toegestane bestandsformaten zijn: JPG, PNG, GIF en PDF.
      </div>
    </div>

    <div
      v-if="
        $parent.intakeAdmin.beleggen.brand === 'VMVB'
        || $parent.intakeAdmin.beleggen.brand === 'BVS'
      "
      class="subheader"
    >
    Bezittingen
    </div>
    <div
      v-if="
        $parent.intakeAdmin.beleggen.brand === 'VMVB'
        || $parent.intakeAdmin.beleggen.brand === 'BVS'
      "
    >
      <!-- Spaarrekening Bedrijf -->
      <customInput
        v-model="modelValue.spaarrekening"
        type="yesno"
        label="Staat er een geldbedrag op betaal- en/of spaarrekening(en) van het bedrijf?"
        :validation="['required']"
      />
      <div
        v-if="modelValue.spaarrekening === 'Yes'"
        class="flex-container flex-grow"
      >
        <customInput
          v-model="modelValue.spaarrekeningBedrag"
          type="money" label="Geldbedrag op betaal- of spaarrekening"
        />
        <customInput
          :options="$parent.banken"
          v-model="modelValue.spaarrekeningInstantie"
          type="dropdown" label="Financiële instelling"
        />
        <customInput
          v-if="modelValue.spaarrekeningInstantie === 'Anders'"
          v-model="modelValue.spaarrekeningInstantieOmschrijving"
          type="text" :validation="['required']"
          label="Naam financiële instelling"
        />
      </div>

      <customInput
        v-if="modelValue.spaarrekening === 'Yes'"
        v-model="modelValue.spaarrekening2"
        type="yesno"
        label="Bezit dit bedrijf nog een betaal- of spaarrekening?"
      />

      <div
        v-if="
          modelValue.spaarrekening === 'Yes'
          && modelValue.spaarrekening2 === 'Yes'
        "
      >
        <div class="flex-container flex-grow">
          <customInput
            v-model="modelValue.spaarrekening2Bedrag"
            type="money" label="Bedrag"
          />
          <customInput
            :options="$parent.banken"
            v-model="modelValue.spaarrekening2Instantie"
            type="dropdown" label="Financiële instelling"
          />
        </div>
        <customInput
          v-if="modelValue.spaarrekening2Instantie === 'Anders'"
          v-model="modelValue.spaarrekening2InstantieOmschrijving"
          type="text" label="Naam financiële instelling"
          :validation="['required']"
        />
        <customInput
          v-if="modelValue.spaarrekening2 === 'Yes'"
          v-model="modelValue.spaarrekening3"
          type="yesno"
          label="Bezit dit bedrijf nog een betaal- of spaarrekening?"
        />
        <template v-if="modelValue.spaarrekening3 === 'Yes'">
          <div class="flex-container flex-grow">
            <customInput
              v-model="modelValue.spaarrekening3Bedrag"
              type="money" label="Bedrag"
            />
            <customInput
              :options="$parent.banken"
              v-model="modelValue.spaarrekening3Instantie"
              type="dropdown" label="Financiële instelling"
            />
          </div>
          <customInput
            v-if="modelValue.spaarrekening3Instantie === 'Anders'"
            v-model="modelValue.spaarrekening3InstantieOmschrijving"
            type="text" label="Naam Financiële instelling"
            :validation="['required']"
          />
        </template>
      </div>

      <!-- Beleggingen Bedrijf -->
      <customInput
        v-model="modelValue.beleggingen"
        type="yesno"
        label="Zijn er beleggingen namens het bedrijf?"
        :validation="['required']"
      />
      <template v-if="modelValue.beleggingen === 'Yes'">
        <div class="flex-container flex-grow">
          <customInput
            v-model="modelValue.beleggingenTypeDienst"
            type="dropdown"
            label="Type dienst"
            :options="beleggingsDienstTypes"
          />
          <customInput
            v-model="modelValue.beleggingenBedrag"
            type="money"
            label="Bedrag"
          />
        </div>
        <div class="flex-container flex-grow">
          <customInput
            v-model="modelValue.beleggingenProfiel"
            type="dropdown"
            label="Profiel"
            :options="$parent.beleggingsProfielen"
          />
          <customInput
            v-model="modelValue.beleggingenInstantie"
            type="dropdown"
            label="Financiële instelling"
            :options="$parent.beleggingsInstanties"
          />
        </div>
        <customInput
          v-if="modelValue.beleggingenInstantie === 'Anders'"
          v-model="modelValue.beleggingenInstantieOmschrijving"
          type="text"
          label="Naam financiële instelling"
        />

        <customInput
          v-model="modelValue.beleggingenMeer"
          type="yesno"
          label="Bezit het bedrijf meer beleggingen?"
        />
        <template v-if="modelValue.beleggingenMeer === 'Yes'">
          <div class="flex-container flex-grow">
            <customInput
              v-model="modelValue.beleggingenMeerTypeDienst"
              type="dropdown"
              label="Type dienst"
              :options="beleggingsDienstTypes"
            />
            <customInput
              v-model="modelValue.beleggingenMeerBedrag"
              type="money"
              label="Bedrag"
            />
          </div>
          <div class="flex-container flex-grow">
            <customInput
              v-model="modelValue.beleggingenMeerProfiel"
              type="dropdown"
              label="Profiel"
              :options="$parent.beleggingsProfielen"
            />
            <customInput
              v-model="modelValue.beleggingenMeerInstantie"
              type="dropdown"
              label="Financiële instelling"
              :options="$parent.beleggingsInstanties"
            />
          </div>
          <customInput
            v-if="modelValue.beleggingenMeerInstantie === 'Anders'"
            v-model="modelValue.beleggingenMeerInstantieOmschrijving"
            type="text"
            label="Naam financiële instelling"
          />
          <customInput
            v-model="modelValue.beleggingenNogMeer"
            type="yesno"
            label="Bezit het bedrijf nog meer beleggingen?"
          />
          <template v-if="modelValue.beleggingenNogMeer === 'Yes'">
            <div class="flex-container flex-grow">
              <customInput
                v-model="modelValue.beleggingenNogMeerTypeDienst"
                type="dropdown"
                label="Type dienst"
                :options="beleggingsDienstTypes"
              />
              <customInput
                v-model="modelValue.beleggingenNogMeerBedrag"
                type="money"
                label="Bedrag"
              />
            </div>
            <div class="flex-container flex-grow">
              <customInput
                v-model="modelValue.beleggingenNogMeerProfiel"
                type="dropdown"
                label="Profiel"
                :options="$parent.beleggingsProfielen"
              />
              <customInput
                v-model="modelValue.beleggingenNogMeerInstantie"
                type="dropdown"
                label="Financiële instelling"
                :options="$parent.beleggingsInstanties"
              />
            </div>
            <customInput
              v-if="modelValue.beleggingenNogMeerInstantie == 'Anders'"
              v-model="modelValue.beleggingenNogMeerInstantieOmschrijving"
              type="text"
              label="Naam financiële instelling"
            />
          </template>
        </template>

      </template>

      <!-- Onroerend goed Bedrijf -->
      <customInput
        v-model="modelValue.onroerendgoed"
        type="yesno"
        label="Is het bedrijf in het bezit van onroerend goed?"
        :validation="['required']"
      />
      <div v-if="modelValue.onroerendgoed === 'Yes'">
        <customInput
          v-model="modelValue.onroerendgoedCommercieel"
          type="yesno"
          label="Wordt dit (deels) voor commerciële doeleinden gebruikt?"
        />
        <div class="flex-container flex-grow">
          <customInput
            v-model="modelValue.onroerendgoedVrijeverkoopwaarde"
            type="money"
            label="Vrije verkoopwaarde onroerend goed"
          />
          <customInput
            v-if="modelValue.onroerendgoedCommercieel === 'Yes'"
            v-model="modelValue.onroerendgoedBedragPerJaar"
            type="money"
            label="Inkomen per jaar onroerend goed"
          />
        </div>
      </div>

      <!-- Overig onroerend goed -->
      <customInput
        v-model="modelValue.overigOnroerendgoed"
        type="yesno"
        label="Is het bedrijf in het bezit van overig onroerend goed?"
      />
      <div v-if="modelValue.overigOnroerendgoed === 'Yes'">
        <customInput
          v-model="modelValue.overigOnroerendgoedObjecten"
          type="number"
          label="Hoeveel objecten bezit het bedrijf in overig onroerend goed?"
        />
        <customInput
          v-model="modelValue.overigOnroerendgoedCommercieel"
          type="yesno"
          label="
            Wordt dit (deels) voor commerciële doeleinden gebruikt gebruikt zoals bijvoorbeeld
            verhuur, maar niet voor eigen verhuur?
          "
        />
        <customInput
          v-if="modelValue.overigOnroerendgoedCommercieel === 'Yes'"
          v-model="modelValue.overigOnroerendgoedCommercieelObjecten"
          type="number"
          :max="Number(modelValue.overigOnroerendgoedObjecten)"
          label="Hoeveel objecten bezit het bedrijf in commerciëel overig onroerend goed?"
        />
        <div class="flex-container flex-grow">
          <customInput
            v-model="modelValue.overigOnroerendgoedVrijeverkoopwaarde"
            type="money"
            label="Vrije verkoopwaarde overig onroerend goed"
          />
          <customInput
            v-if="modelValue.overigOnroerendgoedCommercieel === 'Yes'"
            v-model="modelValue.overigOnroerendgoedBedragPerJaar"
            type="money"
            label="Inkomen per jaar overig onroerend goed"
          />
        </div>
      </div>

      <!-- Hypotheek (overig) onroerend goed -->
      <div
        v-if="
          modelValue.onroerendgoed === 'Yes'
          || modelValue.overigOnroerendgoed === 'Yes'
        "
        class="subheader"
      >
        Hypotheek
      </div>
      <div
        v-if="
          modelValue.onroerendgoed === 'Yes'
          || modelValue.overigOnroerendgoed === 'Yes'
        "
      >
        <customInput
          v-model="modelValue.hypotheekVastgoed"
          type="yesno"
          label="Zit er een hypotheek op het (overig) onroerend goed?"
        />
        <div v-if="modelValue.hypotheekVastgoed === 'Yes'">
          <div class="flex-container flex-grow">
            <customInput
              v-model="modelValue.hypotheekVastgoedType"
              type="dropdown"
              label="Type"
              :options="hypotheekTypes"
            />
            <customInput
              v-model="modelValue.hypotheekVastgoedWaarde"
              :validation="['min']"
              :min="1"
              type="money"
              label="Waarde"
            />
            <customInput
              :options="$parent.hypotheekInstanties"
              v-model="modelValue.hypotheekVastgoedInstantie"
              type="dropdown"
              label="Hypotheek verstrekker"
            />
            <customInput
              v-if="modelValue.hypotheekVastgoedInstantie === 'Anders'"
              v-model="modelValue.hypotheekVastgoedInstantieOmschrijving"
              type="text"
              label="Naam verstrekker"
              :validation="['required']"
            />
            <customInput
              v-model="modelValue.hypotheekVastgoedRenteType"
              type="dropdown"
              label="Rente Type"
              :options="[
                {label:'Vast', value:'vast'},
                {label:'Variabel', value:'variabel'},
              ]"
            />
            <template v-if="modelValue.hypotheekVastgoedRenteType === 'vast'">
              <customInput
                placeholder="Max. 15%"
                v-model="modelValue.hypotheekVastgoedRente"
                type="percentage"
                label="Rentepercentage"
                :max="1500"
                :validation="['max']"
              />
              <customInput
                v-model="modelValue.hypotheekVastgoedLooptijd"
                type="date"
                label="Einddatum rentevaste periode"
                :validation="['min']"
                :minDatetime="new Date()"
                class="flex-grow-double"
              />
            </template>
          </div>

          <customInput
            v-model="modelValue.hypotheekVastgoed2"
            type="yesno"
            label="Zit er nog een hypotheek op het (overig) onroerend goed?"
          />
          <div
            v-if="modelValue.hypotheekVastgoed2 == 'Yes'"
            class="flex-container flex-grow"
          >
            <customInput
              v-model="modelValue.hypotheekVastgoed2Type"
              type="dropdown"
              label="Type"
              :options="hypotheekTypes"
            />
            <customInput
              v-model="modelValue.hypotheekVastgoed2Waarde"
              :validation="['min']"
              :min="1"
              type="money"
              label="Waarde"
            />
            <customInput
              :options="$parent.hypotheekInstanties"
              v-model="modelValue.hypotheekVastgoed2Instantie"
              type="dropdown"
              label="Hypotheek verstrekker"
            />
            <customInput
              v-if="modelValue.hypotheekVastgoed2Instantie === 'Anders'"
              v-model="modelValue.hypotheekVastgoed2InstantieOmschrijving"
              type="text"
              label="Naam verstrekker"
              :validation="['required']"
            />
            <customInput
              v-model="modelValue.hypotheekVastgoed2RenteType"
              type="dropdown"
              label="Rente Type"
              :options="[
                {label:'Vast', value:'vast'},
                {label:'Variabel', value:'variabel'},
              ]"
            />
            <template v-if="modelValue.hypotheekVastgoed2RenteType === 'vast'">
              <customInput
                placeholder="Max. 15%"
                v-model="modelValue.hypotheekVastgoed2Rente"
                type="percentage"
                label="Rentepercentage"
                :max="1500"
                :validation="['max']"
              />
              <customInput
                v-model="modelValue.hypotheekVastgoed2Looptijd"
                type="date"
                label="Einddatum rentevaste periode"
                :validation="['min']"
                :minDatetime="new Date()"
                class="flex-grow-double"
              />
            </template>
          </div>

          <div v-if="modelValue.hypotheekVastgoed2 === 'Yes'">
            <customInput
              v-model="modelValue.hypotheekVastgoed3"
              type="yesno"
              label="Zit er nog een hypotheek op het (overig) onroerend goed?"
            />
            <div
              v-if="modelValue.hypotheekVastgoed3 === 'Yes'"
              class="flex-container flex-grow"
            >
              <customInput
                v-model="modelValue.hypotheekVastgoed3Type"
                type="dropdown"
                label="Type"
                :options="hypotheekTypes"
              />
              <customInput
                v-model="modelValue.hypotheekVastgoed3Waarde"
                :validation="['min']"
                :min="1"
                type="money"
                label="Waarde"
              />
              <customInput
                :options="$parent.hypotheekInstanties"
                v-model="modelValue.hypotheekVastgoed3Instantie"
                type="dropdown"
                label="Hypotheek verstrekker"
              />
              <customInput
                v-if="modelValue.hypotheekVastgoed3Instantie === 'Anders'"
                v-model="modelValue.hypotheekVastgoed3InstantieOmschrijving"
                type="text"
                label="Naam verstrekker"
                :validation="['required']"
              />
              <customInput
                v-model="modelValue.hypotheekVastgoed3RenteType"
                type="dropdown"
                label="Rente Type"
                :options="[
                  {label:'Vast', value:'vast'},
                  {label:'Variabel', value:'variabel'},
                ]"
              />
              <template v-if="modelValue.hypotheekVastgoed3RenteType === 'vast'">
                <customInput
                  placeholder="Max. 15%"
                  v-model="modelValue.hypotheekVastgoed3Rente"
                  type="percentage"
                  label="Rentepercentage"
                  :max="1500"
                  :validation="['max']"
                />
                <customInput
                  v-model="modelValue.hypotheekVastgoed3Looptijd"
                  type="date"
                  label="Einddatum rentevaste periode"
                  :validation="['min']"
                  :minDatetime="new Date()"
                  class="flex-grow-double"
                />
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="
        $parent.intakeAdmin.beleggen.brand === 'VMVB'
        || $parent.intakeAdmin.beleggen.brand === 'BVS'
      "
      class="subheader"
    >
      Statuten
    </div>
    <customInput
      v-model="modelValue.statutenInvest"
      type="yesno"
      label="Staat in de statuten van uw bedrijf dat u met dit vermogen mag beleggen?"
    />
    <customInput
      v-model="modelValue.statuten"
      type="yesno"
      label="Heeft u een kopie van de laatste statuten van het bedrijf?"
    />
    <template v-if="modelValue.statuten == 'Yes'">
      <customInput
        v-model="modelValue.companyFiles"
        type="file"
        :validation="['files']"
        :prefix="'statuten'"
      />
      <div class="msg">
        Toegestane bestandsformaten zijn: JPG, PNG, GIF en PDF.
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'IntakeCompanyFinancePage',
  emits: ['update:model-value'],
  data() {
    return {
      beleggingsDienstTypes: [
        { label: 'Advies', value: 'Advies' },
        { label: 'Vermogensbeheer', value: 'Beheer' },
        { label: 'Zelf beleggen', value: 'Zelf' },
      ],
      hypotheekTypes: [
        { label: 'Annuitair', value: 'annuitair' },
        { label: 'Lineair', value: 'lineair' },
        { label: 'Aflossingsvrij', value: 'aflossingsvrij' },
        { label: 'Spaarhypotheek', value: 'spaarhypotheek' },
      ],
    };
  },
  props: {
    modelValue: Object,
  },
  computed: {
    role() {
      return this.$store.state.role;
    },
    value() {
      return this.modelValue;
    },
  },
};
</script>
